/**
 * Competências — lista (piloto da migração Mantine, U1): competência em
 * andamento em destaque com o próximo passo, abertura com seletor mês/ano +
 * escolha de PERFIL entre os elegíveis do gate (motor direto só como fallback
 * quando nenhum perfil cobre o mês), e a tabela das demais com skeleton/vazio.
 * Toasts para sucesso/erro.
 * 03/09/2026 (pedido do dono): fora da lista e dos rótulos tudo que é ruído
 * técnico de motor — a versão fica na página da competência e na tela Perfis;
 * os Alerts de diagnóstico por perfil ("não elegível para…") saíram (o que
 * bloqueia a abertura segue: problemasGerais e o alerta de "nenhum elegível").
 */
import { useEffect, useState } from "react";
import { Badge, Button, Card, Group, Select, Text, ThemeIcon, Title, Alert } from "@mantine/core";
import { IconCalendarTime } from "@tabler/icons-react";
import { api } from "../api";
import { fmtCompetencia } from "../format";
import { ErroApiAlert } from "../componentes/notificacoes";
import { CompetenciaField } from "../componentes/entradas-mantine";
import { Carregando, Vazio } from "../componentes/Estados";
import { notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { BadgeEstado, ItemEntidade } from "../componentes/listas";
import { navegar } from "../roteador";

interface CompetenciaResumo {
  id: number;
  estado: string;
  tipoCalculo: string;
  /* 03/09/2026 (pedido do dono): perfil/motor fora da listagem — ruído técnico;
     a linha mostra só tipo + contagens. */
  _count: { lancamentos: number; runs: number };
}

const ROTULO_TIPO_CALCULO: Record<string, string> = {
  mensal: "Folha mensal",
  adiantamento_13: "13º — 1ª parcela (adiantamento)",
  decimo_terceiro: "13º — 2ª parcela (dezembro)",
  ferias: "Férias",
};

interface DiagnosticoPerfil {
  perfilId: string;
  nome: string;
  /* 03/09/2026 (pedido do dono): a versão do motor saiu dos rótulos/alertas —
     o dono escolhe o perfil pelo nome; o motor fica no detalhe da tela Perfis. */
  vigenciaInicio: number;
  vigenciaFim: number | null;
  motivo: string | null;
  ativo: boolean;
  vigente: boolean;
  compativel: boolean;
  problemas: string[];
}

/** Resposta do gate por perfil (01/08) — inclui o gate de motores subjacente
 *  para o fallback quando nenhum perfil cobre a competência. */
interface GatePerfis {
  jaExiste: boolean;
  estado: string | null;
  perfis: DiagnosticoPerfil[];
  elegiveis: string[];
  sugerido: string | null;
  gateMotores: {
    elegiveis: string[];
    sugerido: string | null;
    problemasGerais: string[];
    diagnosticos: { motor: string; compativel: boolean; problemas: string[] }[];
  };
}

function competenciaAtual(): number {
  const hoje = new Date();
  return hoje.getUTCFullYear() * 100 + (hoje.getUTCMonth() + 1);
}

/** Próximo passo óbvio da competência (o que o humano faz agora). */
export function proximoPasso(c: CompetenciaResumo): string {
  switch (c.estado) {
    case "aberta":
      return c._count.lancamentos === 0 && c._count.runs === 0
        ? "Importe o ponto e lance as variáveis do mês"
        : "Revise os lançamentos e calcule";
    case "em_calculo":
      return "Gere os arquivos e importe o retorno da Contmega";
    case "conferida":
      return "Sem bloqueantes: feche a competência";
    case "fechada":
      return "Fechada — correções só por retificação";
    case "retificada":
      return "Retifique com os ajustes e feche de novo";
    default:
      return "";
  }
}

export function CompetenciasPage() {
  const [lista, setLista] = useState<CompetenciaResumo[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [novoId, setNovoId] = useState<number | null>(competenciaAtual());
  const [gate, setGate] = useState<GatePerfis | null>(null);
  const [perfilEscolhido, setPerfilEscolhido] = useState("");
  const [motorEscolhido, setMotorEscolhido] = useState("");

  async function carregar() {
    try {
      setErro(null);
      const r = await api.get<{ total: number; competencias: CompetenciaResumo[] }>("/competencias");
      setLista(r.competencias);
    } catch (e) {
      setErro(e);
      notificarErroApi(e, "Falha ao carregar");
    }
  }

  // R5: a abertura é SEMPRE da folha mensal — férias e 13º são cálculos dentro
  // da competência do mês (não há mais seletor de tipo aqui).
  // 01/08: a escolha passa a ser o PERFIL DE PROCESSAMENTO (motor + vigência +
  // motivo); o motor direto fica como fallback quando nenhum perfil cobre o mês.
  async function consultarGate(id: number | null) {
    setNovoId(id);
    setGate(null);
    setPerfilEscolhido("");
    setMotorEscolhido("");
    if (!id) return;
    try {
      const g = await api.get<GatePerfis>(`/competencias/perfis-elegiveis?competencia=${id}&tipoCalculo=mensal`);
      setGate(g);
      if (g.sugerido) setPerfilEscolhido(g.sugerido);
      else if (g.gateMotores.sugerido) setMotorEscolhido(g.gateMotores.sugerido);
    } catch (e) {
      setErro(e);
    }
  }

  async function abrir() {
    try {
      setErro(null);
      await api.post("/competencias", {
        id: novoId,
        perfilId: perfilEscolhido || undefined,
        motorVersion: perfilEscolhido ? undefined : motorEscolhido || undefined,
      });
      notificarOk(`Competência ${fmtCompetencia(novoId!)} aberta.`);
      await carregar();
      setGate(null);
      navegar(`/competencias/${novoId}`);
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  useEffect(() => {
    void carregar();
    void consultarGate(competenciaAtual());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emAndamento = (lista ?? []).find((c) => c.estado !== "fechada") ?? lista?.[0] ?? null;
  const ordenadas = [...(lista ?? [])].sort((a, b) => b.id - a.id);

  return (
    <>
      <Title order={2} mb="md">
        Competências
      </Title>

      {emAndamento && (
        <Card withBorder shadow="xs" mb="md" style={{ borderLeft: "6px solid var(--mantine-color-sillis-7)" }}>
          <Group justify="space-between" wrap="wrap">
            <Group gap="sm">
              <Text fw={700} size="lg">
                {fmtCompetencia(emAndamento.id)}
              </Text>
              <BadgeEstado estado={emAndamento.estado} size="lg" />
              <Text size="sm" c="dimmed">
                {proximoPasso(emAndamento)}
              </Text>
            </Group>
            <Button component="a" href={`#/competencias/${emAndamento.id}`}>
              Continuar de onde parou
            </Button>
          </Group>
        </Card>
      )}

      <Card withBorder shadow="xs" mb="md">
        <Title order={4} mb="sm">
          Abrir uma nova competência
        </Title>
        <Text size="sm" c="dimmed" mb="sm">
          A abertura é sempre da folha mensal — férias e 13º são cálculos dentro da competência do mês, quantas vezes precisar.
        </Text>
        <Group align="flex-end" wrap="wrap">
          <div>
            <Text size="xs" fw={600} mb={2}>
              Competência (mês/ano)
            </Text>
            <CompetenciaField valor={novoId} onChange={(c) => void consultarGate(c)} />
          </div>
          {gate?.jaExiste && (
            <Text size="sm" c="dimmed">
              O mês {fmtCompetencia(novoId!)} já está aberto (<BadgeEstado estado={gate.estado!} />) — férias e 13º são
              cálculos dentro dele.{" "}
              <Button variant="subtle" size="compact-xs" onClick={() => navegar(`/competencias/${novoId}`)}>
                abrir a página do mês
              </Button>
            </Text>
          )}
        </Group>
        {gate && !gate.jaExiste && (
          <>
            {gate.gateMotores.problemasGerais.length > 0 && (
              <Alert color="red" title="Não dá para abrir ainda" mt="sm">
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {gate.gateMotores.problemasGerais.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {/* 03/09/2026 (pedido do dono): os Alerts de diagnóstico POR PERFIL
                ("não elegível para...") saíram — eram o "monte de informações"
                ilegível. O que bloqueia a abertura segue visível: problemasGerais
                acima e, sem nenhum perfil/motor elegível, o Alert vermelho abaixo. */}
            {gate.elegiveis.length > 0 && gate.gateMotores.problemasGerais.length === 0 && (
              <Group align="flex-end" mt="sm">
                <Select
                  label="Perfil de processamento"
                  description="O perfil define o motor e a vigência da parametrização (cadastro e histórico em Perfis)"
                  value={perfilEscolhido}
                  onChange={(v) => setPerfilEscolhido(v ?? "")}
                  data={gate.perfis
                    .filter((d) => d.compativel)
                    .map((d) => ({
                      value: d.perfilId,
                      label: `${d.nome}${d.perfilId === gate.sugerido ? " (sugerido)" : ""}`,
                    }))}
                />
                <Button onClick={() => void abrir()} disabled={!perfilEscolhido}>
                  Abrir competência
                </Button>
              </Group>
            )}
            {gate.elegiveis.length === 0 && gate.gateMotores.elegiveis.length > 0 && gate.gateMotores.problemasGerais.length === 0 && (
              <>
                <Alert color="yellow" title="Nenhum perfil vigente cobre este mês" mt="sm">
                  Escolha a versão do cálculo diretamente — e cadastre um perfil na tela Perfis para fixar a escolha com vigência e motivo registrados.
                </Alert>
                <Group align="flex-end" mt="sm">
                  <Select
                    label="Versão do cálculo"
                    description="a mais recente é a sugerida"
                    value={motorEscolhido}
                    onChange={(v) => setMotorEscolhido(v ?? "")}
                    data={gate.gateMotores.elegiveis.map((m) => ({ value: m, label: `${m}${m === gate.gateMotores.sugerido ? " (sugerido)" : ""}` }))}
                  />
                  <Button onClick={() => void abrir()} disabled={!motorEscolhido}>
                    Abrir competência
                  </Button>
                </Group>
              </>
            )}
            {gate.elegiveis.length === 0 && gate.gateMotores.elegiveis.length === 0 && gate.gateMotores.problemasGerais.length === 0 && (
              <Alert color="red" mt="sm">
                Nenhum perfil/motor compatível com a configuração vigente — a abertura está bloqueada.
              </Alert>
            )}
          </>
        )}
        <ErroApiAlert erro={erro} />
      </Card>

      <Title order={4} mb="sm">
        Todas as competências
      </Title>
      {!lista ? (
        <Carregando linhas={4} />
      ) : ordenadas.length === 0 ? (
        <Vazio texto="Nenhuma competência cadastrada — abra a primeira acima." />
      ) : (
        ordenadas.map((c) => (
          <ItemEntidade
            key={c.id}
            ariaLabel={`Abrir a competência ${fmtCompetencia(c.id)}`}
            onClick={() => navegar(`/competencias/${c.id}`)}
            icone={
              <ThemeIcon variant="light" size="lg" color={c.estado === "fechada" ? "gray" : "sillis"}>
                <IconCalendarTime size={18} />
              </ThemeIcon>
            }
            titulo={fmtCompetencia(c.id)}
            linhaSecundaria={`${ROTULO_TIPO_CALCULO[c.tipoCalculo] ?? c.tipoCalculo} · ${c._count.lancamentos} lançamento(s) · ${c._count.runs} execução(ões)`}
            badges={
              <>
                <BadgeEstado estado={c.estado} />
                <Badge variant="outline" color="gray" size="sm">
                  {proximoPasso(c)}
                </Badge>
              </>
            }
          />
        ))
      )}
    </>
  );
}
